import _ from 'lodash';
import { call, put, takeLatest, select } from 'redux-saga/effects';
import { orgId, Ajax } from 'utils';
import { getParam } from 'utils/ebUtils';
import * as actionTypes from 'state/actions/actionTypes';
import * as currentUserActions from 'state/actions/currentUserActions';
import * as currentUserSelectors from 'state/selectors/currentUserSelectors';

export function* recaptchaVerified({ payload }) {
  try {
    yield put(currentUserActions.recaptchaVerified.success({
      recaptchaVerifiedToken: payload
    }));
  } catch (error) {
    yield put(currentUserActions.recaptchaVerified.fail(error));
  }
}

export function* sendTestNotification({ payload: { contactId, lang, callback } }) {
  try {
    const url = `/${orgId}/${contactId}/testNotification?language=${lang}`;
    const token = yield select(currentUserSelectors.selectRecaptchaVerifedToken);
    const data = new FormData();
    data.append('g-recaptcha-response', token);
    const options = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    };
    const { data: result } = yield call(Ajax.post, url, data, options);

    yield put(currentUserActions.sendTestNotification.success({
      hasSentTestNotification: true,
      sendErrorMsg: (_.isEmpty(result) || result.success) ? '' : result.message
    }));
    if (callback) yield call(callback);
  } catch (error) {
    yield put(currentUserActions.sendTestNotification.fail(error));
  }
}

export function* getProfileInit({ payload: { contactId } }) {
  try {
    const url = `/${orgId}/${contactId}/profile`;
    const { status, data } = yield call(Ajax.get, url);
    if (typeof data !== 'object') {
      yield put(currentUserActions.getProfileInit.fail({}));
    } else {
      const timezones = _.get(data, 'timezones', []);
      const profiles = _.get(data, 'profiles', []);
      const firstNameDetails = _.find(profiles, ['fieldName', 'firstName']);
      const lastNameDetails = _.find(profiles, ['fieldName', 'lastName']);
      const middleInitialDetails = _.find(profiles, ['fieldName', 'middleInitial']);
      const preferredLanguageDetails = _.find(profiles, ['fieldName', 'preferredLanguage']);
      const suffixDetails = _.find(profiles, ['fieldName', 'suffix']);
      const externalIdDetails = _.find(profiles, ['fieldName', 'externalId']);
      const emailDetails = _.find(profiles, ['fieldName', 'email']);
      const timeZoneDetails = _.find(profiles, ['fieldName', 'timeZone']);
      const firstName = _.get(firstNameDetails, 'value', '');
      const lastName = _.get(lastNameDetails, 'value', '');
      const isPreferredLanguageEditable = preferredLanguageDetails?.editable;
      const isPreferredLanguageMandatory = preferredLanguageDetails?.mandatory
      const preferredLanguageList = preferredLanguageDetails?.externalObject?.exposedLanguages ?? [];
      const defaultPreferredLanguage = preferredLanguageDetails?.externalObject?.defaultLanguage ?? '';
      const preferredLanguage = preferredLanguageDetails?.value;
      const middleInitial = _.get(middleInitialDetails, 'value', '');
      const suffix = _.get(suffixDetails, 'value', '');
      const externalId = _.get(externalIdDetails, 'value', '');
      const email = _.get(emailDetails, 'value', '');
      const isEmailEditable = _.get(emailDetails, 'editable', true);
      const timeZone = _.get(timeZoneDetails, 'value', '');
      const isTimeZoneEditable = timeZoneDetails?.editable;
      const isTimeZoneExposed = timeZoneDetails?.expose;
      const isTimeZoneMandatory = timeZoneDetails?.mandatory;
      const timeZoneLabel = timeZoneDetails?.name || 'Time Zone:';
      const showRegistrationLink = _.get(data, 'showRegistrationLink', true);
      const isChangePassword = _.get(data, 'isChangePassword', true);
      const isUseDeviceTimeFrame = _.get(data, 'isUseDeviceTimeFrame', false);

      const profileDetails = {
        timezones,
        firstName,
        lastName,
        preferredLanguage,
        isPreferredLanguageEditable,
        isPreferredLanguageMandatory,
        preferredLanguageList,
        email,
        timeZone,
        middleInitial,
        suffix,
        externalId,
        showRegistrationLink,
        isChangePassword,
        isUseDeviceTimeFrame,
        isEmailEditable,
        isTimeZoneEditable,
        isTimeZoneExposed,
        timeZoneLabel,
        isTimeZoneMandatory,
        profiles: _.reduce(
          profiles,
          (acc, profile) => {
            acc[profile.fieldName] = profile;
            return acc;
          },
          {},
        ),
      };
      const contactPathsResult = yield call(Ajax.get, `/${orgId}/${contactId}/contactPaths`);
      const { data: sendNotificationPermission } = yield call(Ajax.get, `/${orgId}/${contactId}/testNotification/permission`);
      const countries = _.get(contactPathsResult.data, 'countries', []);
      const selectedCountries = _.get(contactPathsResult.data, 'selectedCountries', []);
      const paths = _.get(contactPathsResult.data, 'paths', []);
      const pageServices = _.get(contactPathsResult.data, 'pageServices', []);
      const minContactPathNum = _.get(contactPathsResult.data, 'minContactPathNum');
      const orderMemberPath = _.get(contactPathsResult.data, 'orderMemberPath');
      const registrationEmail = _.get(contactPathsResult.data, 'registrationEmail');
      const formatCountries = countries.map((item) => {
        item.iso2 = _.lowerCase(item.code);
        return item;
      });
      const formatSelectedCountries = selectedCountries.map((item) => {
        item.iso2 = _.lowerCase(item.code);
        return item;
      });

      let emailFlag = true
      const contactPaths = paths.map((item) => {
        if (window.isPublic && location.pathname.indexOf('new/account/profile') != -1) {
          if (item.type == 'EMAIL' && item.editable && registrationEmail && getParam('from') == 'member' && emailFlag) {
            item.value = registrationEmail;
            emailFlag = false
          }
        }
        if (item.type === 'TTYTDD' && !['US', 'CA'].includes(item.countryCode)) {
          item.countryCode = 'US';
        }
        return item;
      });

      const payload = {
        ...profileDetails,
        formatCountries,
        formatSelectedCountries,
        contactPaths,
        registrationEmail,
        pageServices,
        minContactPathNum,
        orderMemberPath,
        sendNotificationPermission,
        hasUniversalProfile: data.hasUniversalProfile
      };
      yield put(currentUserActions.getProfileInit.success(payload));
    }
  } catch (error) {
    yield put(currentUserActions.getProfileInit.fail(error));
  }
}

export function* watchGetProfileInit() {
  yield takeLatest(actionTypes.GET_PROFILE_INIT.ACTION, getProfileInit);
}

export function* watchRecaptchaVerified() {
  yield takeLatest(actionTypes.RECAPTCHA_VERIFIED.ACTION, recaptchaVerified);
}

export function* watchSendTestNotification() {
  yield takeLatest(actionTypes.SEND_TEST_NOTIFICATION.ACTION, sendTestNotification);
}
